import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";
import { useTodaysDate } from "@whoosh/utilities/src/dates";

import {
  lessonTypeFilterAtom,
  startDateFilterAtom,
  timeOfDayFilterAtom,
} from "../../atoms";
import { LessonsFilters } from "../LessonsFilters";
import { LessonsFiltersModalFragment_facility$key } from "./__generated__/LessonsFiltersModalFragment_facility.graphql";
import { LessonsFiltersModalFragment_session$key } from "./__generated__/LessonsFiltersModalFragment_session.graphql";

type Props = {
  sessionRef: LessonsFiltersModalFragment_session$key | null;
  facilityRef: LessonsFiltersModalFragment_facility$key | null;
  isOpen: boolean;
  onClose: () => void;
};
export const LessonsFiltersModal = ({
  sessionRef,
  facilityRef,
  isOpen,
  onClose,
}: Props) => {
  const facility = useFragment(LessonFiltersModalFragmentString, facilityRef);
  const session = useFragment(LessonsFiltersSessionFragmentString, sessionRef);
  const today = useTodaysDate();

  // Filter Atoms
  const [_timeOfDayFilter, _setTimeOfDayFilter] = useAtom(timeOfDayFilterAtom);
  const [_startDateFilter, _setStartDateFilter] = useAtom(startDateFilterAtom);
  const [_lessonTypeIdFilter, _setLessonTypeIdFilter] =
    useAtom(lessonTypeFilterAtom);

  // Local State
  const [timeOfDayFilter, setTimeOfDayFilter] = useState(_timeOfDayFilter);
  const [startDateFilter, setStartDateFilter] = useState(_startDateFilter);
  const [lessonTypeIdFilter, setLessonTypeIdFilter] =
    useState(_lessonTypeIdFilter);

  const onLessonTypeFilterChange = useCallback(
    (lessonTypeId: string, defaultDurationMins: number) => {
      setLessonTypeIdFilter({
        id: lessonTypeId,
        defaultDuration: defaultDurationMins,
      });
    },
    [setLessonTypeIdFilter]
  );

  const onTimeOfDayFilterChange = useCallback(
    ({ timeGTE, timeLTE }: { timeGTE?: string; timeLTE?: string }) => {
      if (timeGTE === undefined && timeLTE === undefined) {
        setTimeOfDayFilter(undefined);
      } else {
        setTimeOfDayFilter({ timeGTE, timeLTE });
      }
    },
    [setTimeOfDayFilter]
  );

  const onStartDateFilterChange = useCallback(
    (time: string) => {
      setStartDateFilter(time);
    },
    [setStartDateFilter]
  );

  const onComplete = () => {
    _setTimeOfDayFilter(timeOfDayFilter);
    _setStartDateFilter(startDateFilter);
    _setLessonTypeIdFilter(lessonTypeIdFilter);
    onClose();
  };

  const onReset = () => {
    _setLessonTypeIdFilter(undefined);
    _setStartDateFilter(undefined);
    _setTimeOfDayFilter(undefined);
    onClose();
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t`Filters`}
      hideFooterPadding
      content={
        <Box px={4}>
          <LessonsFilters
            sessionRef={session ?? null}
            facilityRef={facility ?? null}
            selectedLessonTypeId={lessonTypeIdFilter?.id}
            selectedDate={startDateFilter || today}
            selectedTimeOfDay={timeOfDayFilter}
            onDateFilterChange={onStartDateFilterChange}
            onLessonTypeFilterChange={onLessonTypeFilterChange}
            onTimeOfDayFilterChange={onTimeOfDayFilterChange}
          />
        </Box>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={onReset}
                variant="ghost"
                accessibilityLabel={t`Reset`}
                testID="lessons-filters-modal-reset-button"
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={onComplete}
                accessibilityLabel={t`Save and Close`}
                testID="lessons-filters-modal-save-and-close-button"
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};

const LessonFiltersModalFragmentString = graphql`
  fragment LessonsFiltersModalFragment_facility on Facility {
    ...LessonsFiltersFragment_facility
  }
`;

const LessonsFiltersSessionFragmentString = graphql`
  fragment LessonsFiltersModalFragment_session on Session {
    ...LessonsFiltersFragment_session
  }
`;
